import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { getData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import TimesheetReview from './timesheetReview';
import { getUserRole } from '../../../utils/auth';

const STATUS_DISPLAY = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    PENDING_LEVEL_2: 'Pending Level 2',
};

function buildApprovalHistory(raw) {
    const history = [];
    const fmt = (d) =>
        d
            ? new Date(d).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
              })
            : '';

    if (raw.level1_approver_id && raw.level1_approved_at) {
        history.push({
            actor: raw.level1_approver_name || `User #${raw.level1_approver_id}`,
            action:
                raw.status === 'REJECTED' && raw.rejected_by === raw.level1_approver_id
                    ? 'Rejected'
                    : 'Approved',
            comment: raw.level1_comments || '',
            timestamp: fmt(raw.level1_approved_at),
        });
    }
    if (raw.level2_approver_id && raw.level2_approved_at) {
        history.push({
            actor: raw.level2_approver_name || `User #${raw.level2_approver_id}`,
            action: 'Approved',
            comment: raw.level2_comments || '',
            timestamp: fmt(raw.level2_approved_at),
        });
    }
    if (raw.rejected_by && raw.rejected_at) {
        const alreadyLogged =
            raw.level1_approver_id === raw.rejected_by && raw.level1_approved_at;
        if (!alreadyLogged) {
            history.push({
                actor: raw.rejected_by_name || `User #${raw.rejected_by}`,
                action: 'Rejected',
                comment: raw.rejection_reason || '',
                timestamp: fmt(raw.rejected_at),
            });
        }
    }
    return history;
}

function adaptTimesheetForReview(raw) {
    const emp = raw.employee || {};
    const name = emp.name || '';
    const initials = name
        .split(' ')
        .filter(Boolean)
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    return {
        id: raw.id,
        employeeId: emp.id,
        employeeName: name,
        employeeAvatar: initials,
        employeeRole: emp.user_type || '',
        employeeDept: emp.department_name || emp.team_name || '',
        weekStart: raw.week_start_date,
        weekEnd: raw.week_end_date,
        totalHours: parseFloat(raw.total_hours || 0),
        overtimeHours: parseFloat(raw.total_overtime_hours || 0),
        billableHours: parseFloat(raw.total_billable_hours || 0),
        status: STATUS_DISPLAY[raw.status] || raw.status,
        resubmission_count: raw.resubmission_count || 0,
        submittedOn: raw.submitted_at
            ? new Date(raw.submitted_at).toISOString().split('T')[0]
            : null,
        employeeComment: raw.employee_notes || '',
        level2Required: !!(raw.level2_approver_id || raw.level2_approved_at),
        level1_approver_id: raw.level1_approver_id ?? null,
        level2_approver_id: raw.level2_approver_id ?? null,
        level2_approved_at: raw.level2_approved_at || null,
        rejected_by: raw.rejected_by ?? null,
        rejected_at: raw.rejected_at ?? null,
        rejection_reason: raw.rejection_reason ?? null,
        approvalHistory: buildApprovalHistory(raw),
        // versionHistory: raw.versionHistory || [],  // Phase 2
        entries: (raw.entries || []).map((e) => ({
            ...e,
            day: e.day_type || '',
            date: e.entry_date || '',
            hours: parseFloat(e.hours_worked || 0),
            overtime: parseFloat(e.overtime_hours || 0),
            project: e.project_name || '',
            task: e.task_name || '',
            description: e.work_description || '',
            billable: e.is_billable ? 'Billable' : 'Non-Billable',
            attachments: e.attachment_urls || [],
        })),
    };
}

export default function TimesheetDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [timesheet, setTimesheet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const role = getUserRole();

    const fetchTimesheet = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getData(API_ENDPOINTS.getTimesheetById(id));
            setTimesheet(adaptTimesheetForReview(res.result));
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load timesheet.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) fetchTimesheet();
    }, [id, fetchTimesheet]);

    const handleAction = useCallback(
        async (tsId, decision, comment) => {
            try {
                const url =
                    decision === 'Approve'
                        ? API_ENDPOINTS.approveTimesheet(tsId)
                        : API_ENDPOINTS.rejectTimesheet(tsId);
                await postData(url, { comment: comment || '' });
                await fetchTimesheet();
            } catch (error) {
                setError(error?.response?.data?.message || error);
            }
        },
        [fetchTimesheet]
    );

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <CircularProgress sx={{ color: '#6366f1' }} />
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                    Loading timesheet…
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Typography color="error" sx={{ fontSize: 14 }}>
                    {error}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => navigate(role === 'Manager' ? '/manager/timesheet-management' : '/team-lead/my-timesheet', { state: { returnTab: role === 'Manager' ? 1 : 3 } })}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
        <TimesheetReview
            open={true}
            onClose={() => navigate(role === 'Manager' ? '/manager/timesheet-management' : '/team-lead/my-timesheet', { state: { returnTab: role === 'Manager' ? 1 : 3 } })}
            timesheet={timesheet}
            onAction={handleAction}
            standalone
        />
    );
}
